import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface PendingPremiumRequest {
  id: string;
  plan_name: string;
  amount: number;
  status: string;
  created_at: string;
}

/**
 * Returns the most recent PENDING manual premium request for the current user
 * (or null). Used to short-circuit upgrade CTAs so users with an unapproved
 * request see a "your request is awaiting approval" dialog instead of being
 * pushed to subscribe again.
 */
interface Options {
  /** Polling interval in ms while the consumer needs live updates (e.g. dialog open). */
  refetchInterval?: number | false;
}

export const usePendingPremiumRequest = (options: Options = {}) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["pending-premium-request", user?.id],
    queryFn: async (): Promise<PendingPremiumRequest | null> => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("premium_requests")
        .select("id, plan_name, amount, status, created_at")
        .eq("user_id", user.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as PendingPremiumRequest) ?? null;
    },
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: options.refetchInterval ?? false,
    refetchIntervalInBackground: false,
  });
};
